import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { sortBy } from 'lodash';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, switchMap, tap, toArray } from 'rxjs/operators';
import { filterAndSort } from 'src/app/search.utils';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { possibleBranchRoles } from '../../constants';
import { NotifyService } from '../../service/notify.service';
import { UserAdminService } from '../../service/user-admin.service';
import { UserLoginService } from '../../service/user-login.service';
import { UserSearchService } from '../../service/user-search.service';
import { Branch, BranchRole, SelectableBranchRole, UserProfile, UserSearchResult } from '../../types';

@Component({
  selector: 'app-admin-user-management-branch',
  templateUrl: './admin-user-management-branch.component.html',
  styleUrl: './admin-user-management-branch.component.scss',
  standalone: false,
})
export class AdminUserManagementBranchComponent implements OnInit {
  private adminService = inject(UserAdminService);
  private userSearch = inject(UserSearchService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  public selectedBranch = signal<Branch | undefined>(undefined);

  public branchFilter = signal<string>('');
  public userFilter = signal<string>('');

  public showingCreateUser = signal<boolean>(false);
  public newUserEmail = signal<string>('');
  public newUserName = signal<string>('');

  public hasLoadedUserBranches = signal<boolean>(false);
  public hasLoadedBranchUsers = signal<boolean>(false);

  public branches = signal<Branch[]>([]);
  public users = signal<UserProfile[]>([]);

  public modal = viewChild<PanModalComponent>('addUserModal');
  public modalIsSearching = signal<boolean>(false);
  public modalSearchQuery = signal<string>('');
  public modalUser = signal<UserSearchResult | undefined>(undefined);
  public modalRoles = signal<BranchRole[]>([]);
  public modalFoundUsers$: Observable<UserSearchResult[]>;
  public modalTypeaheadInput$ = new Subject<string>();

  public canAddUsers = computed(() => {
    const branchId = this.selectedBranch()?.id.toString();
    return branchId && this.userLogin.canCreateBranchUsers(branchId);
  });

  public readonly possibleRoles = possibleBranchRoles;

  public branchId = computed(() => this.selectedBranch()?.id.toString());

  public selectableRoles = computed(() =>
    this.possibleRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantBranchPermission(role.id, this.branchId()),
    })),
  );

  public visibleBranches = computed(() => filterAndSort(this.branches(), this.branchFilter()));
  public visibleUsers = computed(() =>
    sortBy(
      this.users().filter(
        (u) =>
          (u.roles.branch[this.branchId()]?.length ?? 0) > 0 &&
          (u.name?.toLowerCase().includes(this.userFilter().toLowerCase()) ||
            u.email?.toLowerCase().includes(this.userFilter().toLowerCase())),
      ),
      (u) => u.name?.toLowerCase() || u.email?.toLowerCase(),
    ),
  );

  public canSubmitModal = computed(() => {
    const hasSelectedUser = !!this.modalUser();
    const wantsToCreateUser = this.showingCreateUser() && this.newUserEmail() && this.newUserName();
    const hasSelectedRoles = this.modalRoles().length > 0;
    return (hasSelectedUser || wantsToCreateUser) && hasSelectedRoles;
  });

  constructor() {
    this.modalFoundUsers$ = concat(
      of([]),
      this.modalTypeaheadInput$.pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        tap(() => this.modalIsSearching.set(true)),
        switchMap((search) => {
          if (!search || search.length < 3) {
            this.modalIsSearching.set(false);
            return of([]);
          }

          return this.userSearch.searchUsersByBranches(search).pipe(
            catchError(() => of([])),
            tap(() => this.modalIsSearching.set(false)),
          );
        }),
      ),
    );
  }

  ngOnInit() {
    this.loadBranches();
  }

  loadBranches(): void {
    this.hasLoadedUserBranches.set(false);
    this.branches.set([]);

    this.adminService.getMyBranches().subscribe((d) => {
      this.branches.set(d);
      this.hasLoadedUserBranches.set(true);
    });
  }

  loadUsers(branch: Branch): void {
    this.hasLoadedBranchUsers.set(false);
    this.users.set([]);

    if (!this.userLogin.canGetBranchUsers(branch.id.toString())) {
      this.hasLoadedBranchUsers.set(true);
      return;
    }

    this.adminService
      .getUsersForBranch(branch.id)
      .pipe(finalize(() => this.hasLoadedBranchUsers.set(true)))
      .subscribe((d) => this.users.set(d.items));
  }

  selectBranch(branch: Branch): void {
    this.userFilter.set('');
    this.selectedBranch.set(branch);
    this.loadUsers(branch);
  }

  addUserRole(user: UserProfile, role: SelectableBranchRole): void {
    this.adminService.grantBranchRole(user.user_id, this.branchId(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name || user.email}" in branch "${this.selectedBranch().name}"`,
      );
    });
  }

  removeUserRole(user: UserProfile, role: SelectableBranchRole): void {
    this.adminService.revokeBranchRole(user.user_id, this.branchId(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name || user.email}" in branch "${this.selectedBranch().name}"`,
      );

      if (user.roles.branch[this.branchId()].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  tryLoadModalUsers(query: string): void {
    if (query.length < 3) return;
    this.modalTypeaheadInput$.next(query);
  }

  async submitModal(): Promise<void> {
    this.hasLoadedBranchUsers.set(false);
    let chosenUser = this.modalUser();

    if (!chosenUser && this.showingCreateUser() && this.newUserEmail() && this.newUserName()) {
      const createdUser = await this.adminService.createUser(this.newUserName().trim(), this.newUserEmail().trim()).toPromise();
      chosenUser = {
        id: createdUser.user_id,
        name: createdUser.name,
        email: createdUser.email,
      };
    }

    concat(...this.modalRoles().map((role) => this.adminService.grantBranchRole(chosenUser.id, this.branchId(), role)))
      .pipe(toArray())
      .pipe(
        finalize(() => {
          this.userFilter.set('');
          this.loadUsers(this.selectedBranch());

          this.modalIsSearching.set(false);
          this.modalRoles.set([]);
          this.modalSearchQuery.set('');

          this.clearInputs();
          this.modal().hide();
        }),
      )
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to branch "${this.selectedBranch().name}"`,
        );
      });
  }

  removeUserPermissions(user: UserProfile): void {
    concat(...user.roles.branch[this.branchId()].map((role) => this.adminService.revokeBranchRole(user.user_id, this.branchId(), role)))
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from branch "${this.selectedBranch().name}"`);
        this.loadUsers(this.selectedBranch());
      });
  }

  // Needs to be an arrow function to bind `this` correctly
  // https://stackoverflow.com/questions/65826059/how-to-call-a-function-when-addtag-is-used-in-ng-select
  addNewUser = (newUserString: string) => {
    this.modalUser.set(undefined);
    this.showingCreateUser.set(true);

    const [name] = newUserString.split('@');
    this.newUserName.set(name);
    if (newUserString.includes('@')) {
      this.newUserEmail.set(newUserString);
    }

    // Prevent ng-select from creating a tag
    return false;
  };

  public clearInputs() {
    this.showingCreateUser.set(false);
    this.modalUser.set(undefined);
    this.newUserEmail.set('');
    this.newUserName.set('');
  }
}
